import { EMPTY_ARRAY } from '../content/const'
import { buildAssetsURL } from '#internal/nuxt/paths'
import type { NuxtAppManifestMeta } from '#app'
import type { FetchError } from 'ofetch'

export const checkNewVersionAvailable = async (callback?: () => void | Promise<void>) => {
  const nuxtApp = useNuxtApp()
  let currentManifest: NuxtAppManifestMeta | undefined
  try {
    currentManifest = await getAppManifest()
  } catch (e) {
    const err = e as FetchError | Error
    // The build is already outdated but the manifest was not cached
    if (!('status' in err && (err.status === 404 || err.status === 403))) {
      throw err
    }
  }
  if (callback) {
    await callback()
  }
  try {
    const meta = await $fetch<NuxtAppManifestMeta>(
      buildAssetsURL('builds/latest.json') + `?${Date.now()}`
    )
    if (meta.id !== currentManifest?.id) {
      // There is a newer build which we will let the user handle
      nuxtApp.hooks.callHook('app:manifest:update', meta)
      return true
    }
    nuxtApp.hooks.callHook('app:manifest:update:check')
    return false
  } catch {
    return false
  }
}

export const useVersion = () => {
  const isNewVersionAvailable = useState('isNewVersionAvailable', () => false)
  const checkVersionCallbacks = useState<{ [key: string]: (() => void | Promise<void>)[] }>(
    'check-version-callbacks',
    () => ({})
  )
  const id = useId()
  const nuxtApp = useNuxtApp()
  nuxtApp.hooks.hookOnce('app:manifest:update', () => {
    isNewVersionAvailable.value = true
  })
  nuxtApp.hooks.hook('app:manifest:update:check', () => {
    const callbacks = checkVersionCallbacks.value[id] ?? EMPTY_ARRAY
    for (let i = 0; i < callbacks.length; i++) {
      const cb = callbacks[i]!
      cb()
    }
  })
  onBeforeUnmount(() => {
    delete checkVersionCallbacks.value[id]
  })
  const onCheck = (cb: () => void | Promise<void>) => {
    if (!checkVersionCallbacks.value[id]) {
      checkVersionCallbacks.value[id] = []
    }
    checkVersionCallbacks.value[id]?.push(cb)
  }
  return {
    isNewVersionAvailable: readonly(isNewVersionAvailable),
    onCheck,
  }
}
